import { toast } from "react-toastify";
import { takeEvery, call, put, select, all } from "redux-saga/effects";

import {
  getOrderItems,
  createOrder,
  createOrderItem,
  putOrderItem,
  deleteOrderItem,
  attachOrderToCustomer,
} from "@/services/OrderService";

import Order from "@/redux/models/Order";
import Items from "@/redux/models/Items";
import Customer from "@/redux/models/Customer";
import { getRecoverOrder } from "../../services/OrderService";

const { updateItem, postItems, deleteItems, deleteItem } = Items.types;
const { create, postOrder, retrieveOrder, attachToCustomer, recoverOpenOrder } = Order.types;

function* getOrderSaga(action) {
  const { orderId, cb, onRedirect } = action.payload;
  try {
    const order = yield call(getOrderItems, orderId);
    yield put(create(order));

    if (cb) cb(order);
  } catch (error) {
    const customer = yield select(Customer.selectors.getCustomer);

    yield put(Order.types.clear());

    if ([401, 403].includes(error?.response?.status)) {
      if (!customer?.customer_id) {
        toast.error("You must be logged to perform this action.", {
          toastId: "request-error",
          updateId: "request-error",
        });
        onRedirect?.(
          `/auth/signin?${new URLSearchParams({
            redirectUrl: window.location.pathname,
          })}`
        );
      } else {
        toast.error("You do not have permission to perform this action.", {
          toastId: "request-error",
          updateId: "request-error",
        });
        onRedirect?.("/");
      }
    } else {
      toast.error("Something went wrong!", { toastId: "request-error", updateId: "request-error" });
    }
  }
}

function* postOrderSaga(action) {
  try {
    const { design } = action.payload;
    const order = yield call(createOrder, { design });

    yield put(create({ ...order, id: order.pk }));
  } catch (err) {
    if (err?.response?.data?.non_field_errors)
      toast.error(err?.response?.data?.non_field_errors[0]);
    else toast.error("Something went wrong!");
  }
}

function* recoverOpenOrderSaga(action) {
  try {
    const order = yield select(Order.selectors.getOrder);

    if (order?.id) return;

    const recoveredOrder = yield call(getRecoverOrder);

    yield put(create(recoveredOrder));

    if (action?.payload?.cb) action?.payload?.cb?.(recoveredOrder);
  } catch {
    action?.payload?.cb?.(false);
  }
}

function* postItemSaga(action) {
  try {
    const { attributes, quantity, description, hcard_design, cb } = action.payload;
    const { Order, Checkout } = yield select();
    const order = Order.itemsById[Order.items[0]];

    const newOrder = yield call(createOrderItem, {
      order: order.id,
      design: { id: Checkout.itemsById[0].selectedDesign },
      attributes,
      quantity,
      description,
      hcard_design,
    });
    yield put(create(newOrder));

    if (cb) cb();
  } catch (err) {
    if (err?.response?.data?.non_field_errors)
      toast.error(err?.response?.data?.non_field_errors[0]);
    else toast.error("Something went wrong!");
  } finally {
    action.payload?.finallyCb?.();
  }
}

function* deleteItemSaga(action) {
  try {
    const { id, callback } = action.payload;
    const newOrder = yield call(deleteOrderItem, { id });

    yield put(create(newOrder));

    if (callback) callback(newOrder);
  } catch (err) {
    if (err?.response?.data?.non_field_errors)
      toast.error(err?.response?.data?.non_field_errors[0]);
    else toast.error(err?.message || "Something went wrong!");
  } finally {
    action?.payload?.finallyCb?.();
  }
}

function* deleteItemsSaga(action) {
  try {
    const { items, callback } = action.payload;

    const orderAfterItemRemoval = yield all(
      Object.assign(
        {},
        items?.map((id) => call(deleteOrderItem, { id }))
      )
    );

    const newOrder = orderAfterItemRemoval?.[items?.length - 1];

    yield put(create(newOrder));

    if (callback) callback(newOrder);
  } catch (err) {
    console.log(err);
    if (err?.response?.data?.non_field_errors)
      toast.error(err?.response?.data?.non_field_errors[0]);
    else toast.error("Something went wrong!");
  } finally {
    action?.payload?.finallyCb?.();
  }
}

function* updateItemSaga(action) {
  try {
    const { attributes, quantity, description, id, cb } = action.payload;
    const newOrder = yield call(putOrderItem, {
      id,
      attributes,
      quantity,
      description,
    });

    yield put(create(newOrder));

    cb?.(newOrder);
  } catch (err) {
    if (err?.response?.data?.non_field_errors)
      toast.error(err?.response?.data?.non_field_errors[0]);
    else toast.error("Something went wrong!");
  } finally {
    action?.payload?.finallyCb?.();
  }
}

function* attachToCustomerSaga(action) {
  try {
    const order = yield select(Order.selectors.getOrder);

    if (!order?.id) return;

    if (!order?.customer) yield call(attachOrderToCustomer, order.id);

    yield put(retrieveOrder({ orderId: order.id }));

    if (action?.payload?.cb) action?.payload?.cb?.();
  } catch (err) {
    if (err?.response?.data?.non_field_errors)
      toast.error(err?.response?.data?.non_field_errors[0]);
    else toast.error("Something went wrong!");
  }
}

export default function* watchApp() {
  yield takeEvery(retrieveOrder().type, getOrderSaga);
  yield takeEvery(postOrder().type, postOrderSaga);
  yield takeEvery(postItems().type, postItemSaga);
  yield takeEvery(deleteItem().type, deleteItemSaga);
  yield takeEvery(deleteItems().type, deleteItemsSaga);
  yield takeEvery(updateItem().type, updateItemSaga);
  yield takeEvery(attachToCustomer().type, attachToCustomerSaga);
  yield takeEvery(recoverOpenOrder().type, recoverOpenOrderSaga);
}
